import { createContext, useContext, useMemo, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  buildPlans,
  DEFAULT_PLAN_LIMITS,
  type PlanKey,
  type PlanConfig,
  type AllPlanLimits,
  type PlanLimitsConfig,
} from "@/config/plans";
import {
  FREE_ACCESS_LIMITS_DEFAULTS,
  FREE_ACCESS_SETTINGS_KEYS,
  type FreeAccessLimits,
} from "@/config/free-access";

interface FreeAccessContextType {
  /** Whether free access mode is currently enabled */
  isFreeAccessMode: boolean;
  /** Dynamic limits for free-access users (from DB or defaults) */
  freeAccessLimits: FreeAccessLimits;
  /** True while the initial settings are being fetched */
  isLoading: boolean;
  /** Plan configurations resolved for the current mode + dynamic limits */
  plans: Record<PlanKey, PlanConfig>;
  /** Invalidate the cached settings (call after admin changes) */
  invalidate: () => void;
}

const FreeAccessContext = createContext<FreeAccessContextType | undefined>(undefined);

const PLAN_SETTINGS_QUERY_KEY = ["plan-settings"] as const;

/** Parse a platform_settings value to a number, returning the fallback on failure. */
function parseNumericSetting(value: unknown, fallback: number): number {
  if (typeof value === "number") return value;
  const parsed = parseInt(String(value), 10);
  return Number.isNaN(parsed) ? fallback : parsed;
}

/**
 * Parse limits for a single plan from the settings map.
 *
 * DB key patterns:
 *   {plan}_max_stores            → max_stores
 *   {plan}_max_products          → max_products
 *   {plan}_ai_optimizations_monthly → ai_optimizations_monthly
 *   {plan}_max_ai_chats_monthly  → ai_chat_messages_monthly  (note: different suffix)
 *   {plan}_max_bulk_operations_monthly → max_bulk_operations_monthly
 */
function parsePlanLimits(
  data: Map<string, unknown>,
  plan: PlanKey,
): PlanLimitsConfig {
  const d = DEFAULT_PLAN_LIMITS[plan];
  return {
    max_stores: parseNumericSetting(data.get(`${plan}_max_stores`), d.max_stores),
    max_products: parseNumericSetting(data.get(`${plan}_max_products`), d.max_products),
    ai_optimizations_monthly: parseNumericSetting(
      data.get(`${plan}_ai_optimizations_monthly`),
      d.ai_optimizations_monthly,
    ),
    ai_chat_messages_monthly: parseNumericSetting(
      data.get(`${plan}_max_ai_chats_monthly`),
      d.ai_chat_messages_monthly,
    ),
    max_bulk_operations_monthly: parseNumericSetting(
      data.get(`${plan}_max_bulk_operations_monthly`),
      d.max_bulk_operations_monthly,
    ),
  };
}

export function FreeAccessProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  // Fetch ALL platform_settings so we can derive limits for every plan tier.
  const { data, isLoading } = useQuery({
    queryKey: PLAN_SETTINGS_QUERY_KEY,
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from("platform_settings")
        .select("key, value");

      if (error || !rows) return null;

      return new Map(rows.map((r) => [r.key, r.value]));
    },
    staleTime: 5 * 60 * 1000, // 5 minutes — matches backend cache TTL
  });

  const isFreeAccessMode = useMemo(() => {
    if (!data) return false;
    const v = data.get("free_access_mode");
    return v === "true" || v === true;
  }, [data]);

  const freeAccessLimits: FreeAccessLimits = useMemo(() => {
    if (!data) return FREE_ACCESS_LIMITS_DEFAULTS;
    return {
      max_stores: parseNumericSetting(
        data.get(FREE_ACCESS_SETTINGS_KEYS.max_stores),
        FREE_ACCESS_LIMITS_DEFAULTS.max_stores,
      ),
      max_products: parseNumericSetting(
        data.get(FREE_ACCESS_SETTINGS_KEYS.max_products),
        FREE_ACCESS_LIMITS_DEFAULTS.max_products,
      ),
      ai_optimizations_monthly: parseNumericSetting(
        data.get(FREE_ACCESS_SETTINGS_KEYS.ai_optimizations_monthly),
        FREE_ACCESS_LIMITS_DEFAULTS.ai_optimizations_monthly,
      ),
      ai_chat_messages_monthly: parseNumericSetting(
        data.get(FREE_ACCESS_SETTINGS_KEYS.ai_chat_messages_monthly),
        FREE_ACCESS_LIMITS_DEFAULTS.ai_chat_messages_monthly,
      ),
      max_bulk_operations_monthly: parseNumericSetting(
        data.get(FREE_ACCESS_SETTINGS_KEYS.max_bulk_operations_monthly),
        FREE_ACCESS_LIMITS_DEFAULTS.max_bulk_operations_monthly,
      ),
    };
  }, [data]);

  const allPlanLimits: AllPlanLimits = useMemo(() => {
    if (!data) return DEFAULT_PLAN_LIMITS;
    return {
      free: parsePlanLimits(data, "free"),
      growth: parsePlanLimits(data, "growth"),
      pro: parsePlanLimits(data, "pro"),
      agency: parsePlanLimits(data, "agency"),
    };
  }, [data]);

  const plans = useMemo(
    () => buildPlans(isFreeAccessMode, freeAccessLimits, allPlanLimits),
    [isFreeAccessMode, freeAccessLimits, allPlanLimits],
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [...PLAN_SETTINGS_QUERY_KEY] });
  };

  return (
    <FreeAccessContext.Provider
      value={{ isFreeAccessMode, freeAccessLimits, isLoading, plans, invalidate }}
    >
      {children}
    </FreeAccessContext.Provider>
  );
}

export function useFreeAccess() {
  const context = useContext(FreeAccessContext);
  if (context === undefined) {
    throw new Error("useFreeAccess must be used within a FreeAccessProvider");
  }
  return context;
}
